import type { Deal } from './types';

/**
 * Day 1 is a session, not a bare exercise: a simple subject, a one-minute
 * talk to warm up, then Ten Bad Ideas on the same subject, one at a time,
 * then the card, the share, and the road ahead. The warm-up is a Loose
 * exercise in v2 terms (it takes nothing) and its recording stays on the device.
 */
export interface Warmup {
  id: string;
  title: string;
  /** how long the talk runs */
  seconds: number;
  /** "That's enough" appears after this */
  minSeconds: number;
  /** the only three things the ring ever says (owner, 2026-09-21: the question
   *  prompts — "What was in the bag?" — were more to take in than they helped) */
  prompts: { keep: string; next: string; end: string };
  /** "What next?" comes up every `every` seconds, for `nudge` seconds */
  nudge: number;
  every: number;
}

export interface Session {
  id: string;
  subjects: string[];
  warmup: Warmup;
  exerciseId: string;
  /** which deal key the subject fills for the exercise */
  dealKey: string;
  /** softer Day 1 wording for the exercise; {key} is the deal key */
  copy: { prompt: string; headline: string; hint: string; placeholder: string; encouragement: string };
}

export const TALK_ABOUT: Warmup = {
  id: 'talk-about',
  title: 'Talk for a minute',
  seconds: 75,
  minSeconds: 20,
  prompts: { keep: 'Keep talking', next: 'What next?', end: 'How did it end?' },
  nudge: 5,
  /** a nudge every 15 seconds; "How did it end?" takes over for the last 15 */
  every: 15,
};

export const DAY_ONE: Session = {
  id: 'day-one',
  // place-shaped, so "Once upon a time… at the airport." reads
  subjects: ['the airport', 'the dentist', 'a wedding', 'a Monday meeting', 'the supermarket', 'the gym', 'a hotel', 'the station', 'the car park', 'the post office'],
  warmup: TALK_ABOUT,
  exerciseId: 'ten-bad-ideas',
  dealKey: 'situation',
  // "Ideas to change {situation}" arrives seconds after the person has told a
  // story about that same place, and a tester read it as "change your story"
  // (interview 2026-09-18). The exercise's own wording — bad ideas, making the
  // place more fun — cannot be read that way, so Day 1 keeps it and only drops
  // "terrible" for "bad".
  copy: {
    prompt: 'Ten bad ideas for making {situation} more fun.',
    headline: '{n} bad idea{s} for {situation}',
    hint: 'Bad ideas are welcome. Silly counts. Ten is the aim, not the rule.',
    placeholder: 'An idea, any idea',
    encouragement: 'Nothing here can be wrong.',
  },
};

export function drawSubject(s: Session, avoid?: string): string {
  const pool = s.subjects.filter((x) => x !== avoid);
  return pool[Math.floor(Math.random() * pool.length)];
}

// "Once upon a time… at the airport", but "in a Monday meeting": the few
// subjects you are inside rather than at.
const INSIDE = new Set(['a Monday meeting', 'a hotel', 'the car park']);
export function placeOf(subject: string): string {
  return `${INSIDE.has(subject) ? 'in' : 'at'} ${subject}`;
}

export function dealForSubject(s: Session, subject: string): Deal {
  return { [s.dealKey]: subject };
}
